/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '../../../assets/ts/_utils'
import {useThemeMode} from '../../layout/theme-mode/ThemeModeProvider'
import {useStore} from '../../../../app/modules/scheduler/store'
import {
  addDays,
  addMinutes,
  eachMinuteOfInterval,
  endOfDay,
  format,
  formatDuration,
  isBefore,
  startOfDay,
  startOfWeek,
} from 'date-fns'
import {
  filterEventsBetween,
  getHoursOfOperationOnDay,
  getResidualSeriesColor,
  getTrackedRoleClasses,
  isPositionInRoleClass,
  isTrackedSeparately,
} from '../../../../app/modules/scheduler/helpers/generals'
import {getChartData} from '../../../../app/modules/scheduler/core/_requests'
import {ChartDataModel} from '../../../../app/modules/scheduler/core/_models'
import {formatMeasureValue, stripSuffix} from '../../../../app/modules/scheduler/core/_formats'
import {getAxisTickAmount} from './chartAxis'

type Props = {
  className: string
}

type LineSeries = {name: string; data: number[]; format?: string}

// The chart wrapper keeps this height whether or not a chart is mounted, so
// the card body's expand/collapse animation always covers real content height.
const CHART_HEIGHT = 275
const BODY_PADDING = 24

// Line-series colors (one per config measure), cycled when a config defines
// more measures than colors. The assigned-crew columns keep their own
// role-class colors.
const LINE_COLORS = ['#E91E63', '#004F2D', '#980000', '#1B84FF', '#7239EA', '#F6C000', '#17C653']

// Width granularity that triggers a chart rebuild while resizing.
const WIDTH_QUANTUM = 40

// Shown until the config's own name arrives with the data.
const FALLBACK_TITLE = 'Summary Plot'

const ChartSummary: React.FC<Props> = ({className}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {week, selectedDate, events, locale, hourFormat, hoursOfOperations, selectedLocationId} =
    useStore()
  const [chartCollapsed, setChartCollapsed] = useState(true)
  // True only while the body is FULLY expanded (set after the CSS transition
  // ends). The chart mounts on true and is destroyed on false — so the
  // open/close height animation never runs concurrently with chart work.
  const [bodySettled, setBodySettled] = useState(false)
  // Legend toggles the user made, kept across chart rebuilds (collapse/expand,
  // shift edits, day changes) — each rebuild re-applies this instead of
  // resetting to the defaults.
  const hiddenSeriesRef = useRef<Set<string>>(new Set())
  // Line names we've already applied the "start hidden" default to, so a line
  // is hidden on first appearance but keeps the user's later toggle. New
  // measures added to the config appear hidden the first time, like the rest.
  const seenLinesRef = useRef<Set<string>>(new Set())
  // The config-driven df (per-hour rows, one column per measure) that the line
  // series are built from — slug summary_plot_lines, via getChartData.
  const [chartData, setChartData] = useState<ChartDataModel | undefined>(undefined)
  // Container width, quantized. ApexCharts resizes its own SVG, but the x-axis
  // label density is decided at render time — this rebuilds the chart when the
  // card actually gets narrower/wider (window resize, sidebar toggle). Rounded
  // so a slow drag doesn't rebuild on every pixel.
  const [chartWidth, setChartWidth] = useState(0)
  const {weekStartOn, weekDays, startHour, endHour, step, cellRenderer} = week!

  useEffect(() => {
    const el = chartRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const observed = entries[0]?.contentRect.width ?? 0
      setChartWidth(Math.round(observed / WIDTH_QUANTUM) * WIDTH_QUANTUM)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    let active = true
    if (selectedLocationId) {
      getChartData('summary_plot_lines', selectedLocationId, selectedDate).then((data) => {
        if (active) setChartData(data)
      })
    }
    return () => {
      active = false
    }
  }, [selectedLocationId, selectedDate])

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    // use integer part of startHour for hour and decimal part for minutes
    // get decimal part of startHour
    const [dayStart, dayEnd] = getHoursOfOperationOnDay(selectedDate, hoursOfOperations)
    const START_TIME = dayStart //setMinutes(setHours(selectedDate, startHour), startHourDecimal * 60)
    // use integer part of endHour for hour and decimal part for minutes
    // get decimal part of endHour
    const END_TIME = addMinutes(dayEnd, -30) //setMinutes(setHours(selectedDate, endHour), endHourDecimal * 60)

    // if END_TIME is before START_TIME, it means that the day is over midnight
    // so we need to add 24 hours to the end time
    if (isBefore(END_TIME, START_TIME)) {
      END_TIME.setHours(END_TIME.getHours() + 24)
    }

    const hours = eachMinuteOfInterval(
      {
        start: START_TIME,
        end: END_TIME,
      },
      {step: step}
    )
    const hFormat = hourFormat === '12' ? 'h:mm a' : 'H:mm'

    // Assigned-crew series are data-driven: one series per role class flagged
    // track_hours_separately (legacy "Assigned GM"/"Assigned SL"), plus one
    // residual series for everything else. Position keys are canonical
    // (server-side alias resolution), so no spelling variants to handle.
    const trackedClasses = getTrackedRoleClasses()
    const headcounts = (filter: (position: string | undefined) => boolean) =>
      hours.map(
        (h) =>
          filterEventsBetween(
            events.filter((x: any) => filter(x.position)),
            h,
            addMinutes(h, step)
          ).length
      )
    const assignedSeries: {name: string; data: number[]; color: string}[] = [
      {
        name: 'Assigned Employees',
        data: headcounts((position) => !isTrackedSeparately(position)),
        color: getResidualSeriesColor(),
      },
      ...trackedClasses.map((cls) => ({
        name: `Assigned ${cls.name}`,
        data: headcounts((position) => isPositionInRoleClass(position, cls.key)),
        color: cls.color,
      })),
    ]

    // The line series are entirely config-driven: one line per measure column
    // in the summary_plot_lines config (its title = the series name, its format
    // code = the y-axis format). Nothing here is hardcoded to a particular
    // measure name, so editing the config (rename / add / remove a measure,
    // change a format) is reflected on the plot — a per-manhour line is just
    // another measure in the config, not something this component derives.
    const cols = chartData?.columns ?? []
    const formats = chartData?.formats ?? {}
    const dimCol = cols[0]
    const measureCols = cols.slice(1)
    const parseHour = (label: string): number => {
      const m = label.match(/(\d{1,2}):\d{2}(?:\s*(AM|PM))?/i)
      if (!m) return -1
      let h = parseInt(m[1], 10)
      if (m[2]) {
        h = h % 12
        if (/PM/i.test(m[2])) h += 12
      }
      return h
    }
    // hour (0-23) -> { rawColumnName: value } for every measure column.
    const byHour: {[hour: number]: {[col: string]: number}} = {}
    ;(chartData?.rows ?? []).forEach((r) => {
      const h = parseHour(String(r[dimCol]))
      const slot: {[col: string]: number} = {}
      measureCols.forEach((c) => {
        slot[c] = Number(r[c]) || 0
      })
      byHour[h] = slot
    })

    // One line per measure column, aligned to the visible hour slots.
    const lineSeries: LineSeries[] = measureCols.map((col) => ({
      name: stripSuffix(col),
      format: formats[col],
      data: hours.map((hour) => byHour[hour.getHours()]?.[col] ?? 0),
    }))

    // Every line starts hidden the first time it appears (the columns start
    // visible); the user's later legend toggles are remembered across rebuilds.
    lineSeries.forEach((s) => {
      if (!seenLinesRef.current.has(s.name)) {
        seenLinesRef.current.add(s.name)
        hiddenSeriesRef.current.add(s.name)
      }
    })

    const hoursStr = hours.map((h) => format(h, hFormat, {locale}))
    const height = parseInt(getCSS(chartRef.current, 'height'))
    const width = chartRef.current.clientWidth

    // A previous chart's async render() can outlive its destroy() and leave an
    // orphan SVG behind (stacked duplicate charts) — start from a clean slate.
    chartRef.current.innerHTML = ''

    const hiddenSeries = hiddenSeriesRef.current
    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, width, hoursStr, assignedSeries, lineSeries, hiddenSeries)
    )
    if (chart) {
      chart.render()
      // Synchronously, before the first paint settles: hiding in
      // render().then() paints one frame with every series visible first — a
      // visible flash on each rebuild.
      try {
        const seriesNames = [...assignedSeries.map((s) => s.name), ...lineSeries.map((s) => s.name)]
        seriesNames.forEach((name) => {
          if (hiddenSeries.has(name)) {
            chart.hideSeries(name)
          }
        })
      } catch (e) {
        // chart torn down mid-render — nothing to hide
      }
    }

    return chart
  }
  const {mode} = useThemeMode()

  useEffect(() => {
    // if scheduleDaySlotStats's forecast_values' any key date is not equal to selectedDate, then wait for the scheduleDaySlotStats to be updated
    // Chart work (per-slot series computation + ApexCharts render) is heavy
    // main-thread work — it only runs while the body is fully open, never
    // during the open/close height transition.
    if (!bodySettled) {
      return
    }
    const chart = refreshChart()
    return () => {
      if (chart) {
        chart.destroy()
      }
      // destroy() can miss the SVG of a render that was still in flight —
      // clear the container so orphans never stack up.
      if (chartRef.current) {
        chartRef.current.innerHTML = ''
      }
    }
  }, [events, selectedDate, mode, chartData, bodySettled, chartWidth])

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          {/* The card titles itself from the chart config (name/description),
              so renaming the config renames the widget. The fallbacks show
              until the first response arrives. */}
          <span className='card-label fw-bold fs-3 mb-1'>{chartData?.name || FALLBACK_TITLE}</span>

          <span className='text-muted fw-semibold fs-7'>
            {chartData?.description || `Scheduled Labor on ${format(selectedDate, 'M/d/yyyy')}`}
          </span>
        </h3>

        {/* begin::Toolbar */}
        <div className='card-toolbar' data-fds-buttons='true'>
          {/* React owns the expand/collapse entirely (CSS max-height below).
              The old data-bs-toggle collapse fought React's re-render — React
              reset the className mid-animation, so the card opened/closed in
              two jerky phases. */}
          <button
            className='btn btn-icon btn-secondary'
            type='button'
            onClick={() => {
              setChartCollapsed(!chartCollapsed)
            }}
            aria-expanded={!chartCollapsed}
            aria-controls='fds-chart-body'
          >
            <i className={`fas ${chartCollapsed ? 'fa-arrow-down' : 'fa-arrow-up'}`}></i>
          </button>
        </div>
        {/* end::Toolbar */}
      </div>
      {/* end::Header */}

      {/* begin::Body — pure CSS height transition; the chart mounts only after
          the expand settles (onTransitionEnd → bodySettled) and is destroyed
          only after the collapse finishes, so the slide itself never competes
          with chart rendering. The chart wrapper has a FIXED height so the
          body's real height equals the animated max-height even while the
          chart isn't mounted yet — otherwise the slide runs over an empty
          (padding-high) body and the content pops in with a jump at the end. */}
      <div
        className='card-body'
        id='fds-chart-body'
        style={{
          maxHeight: chartCollapsed ? 0 : CHART_HEIGHT + 2 * BODY_PADDING,
          overflow: 'hidden',
          paddingTop: chartCollapsed ? 0 : BODY_PADDING,
          paddingBottom: chartCollapsed ? 0 : BODY_PADDING,
          transition: 'max-height 0.3s ease, padding 0.3s ease',
        }}
        onTransitionEnd={(e) => {
          if (e.propertyName !== 'max-height') return
          setBodySettled(!chartCollapsed)
        }}
      >
        {/* begin::Chart */}
        <div
          ref={chartRef}
          id='fds_charts_widget_summary_chart'
          style={{height: CHART_HEIGHT}}
        ></div>
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export {ChartSummary}

function getChartOptions(
  height: number,
  width: number,
  hours: string[],
  assignedSeries: {name: string; data: number[]; color: string}[],
  lineSeries: LineSeries[],
  hiddenSeries: Set<string>
): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-900')
  const borderColor = getCSSVariableValue('--bs-gray-200')

  // A line's y-axis is only rendered while the line itself is visible, so the
  // space left for x-axis labels depends on the current legend state.
  const visibleLineAxes = lineSeries.filter((s) => !hiddenSeries.has(s.name)).length
  const tickAmount = getAxisTickAmount(width, hours.length, visibleLineAxes)

  // Axis range from the data itself: ignore non-finite values, keep 5%
  // headroom above the peak, follow the data below zero if it ever goes there.
  function axisBounds(values: number[]) {
    const finite = values.filter((v) => Number.isFinite(v))
    const max = finite.length ? Math.max(...finite) : 0
    const min = finite.length ? Math.min(0, ...finite) : 0
    return {min, max: max > 0 ? max * 1.05 : 1}
  }

  // Stacked-columns axis ceiling: the sum of every assigned series' peak.
  const assignedMax =
    assignedSeries.reduce((acc, s) => acc + (s.data.length ? Math.max(...s.data) : 0), 0) + 1
  const assignedCount = assignedSeries.length
  const assignedIntLabels = {
    formatter: function (val: number) {
      return val.toFixed(0)
    },
    style: {
      colors: labelColor,
      fontSize: '12px',
    },
  }

  return {
    series: [
      // One column series per assigned group (residual crew + each role class
      // tracked separately). The shared group is what actually stacks the
      // columns on ApexCharts >=3.45 — with each series bound to its own
      // y-axis, `stacked: true` alone no longer does it.
      ...assignedSeries.map((s) => ({
        name: s.name,
        type: 'column',
        group: 'assigned',
        data: s.data,
      })),
      // One line per config measure.
      ...lineSeries.map((s) => ({
        name: s.name,
        type: 'line',
        data: s.data,
      })),
    ],
    chart: {
      height: 275,
      type: 'line',
      stacked: true,
      // Only stack the employee columns — without this, the stat lines stack
      // cumulatively onto each other and overshoot their own y-axes.
      stackOnlyBar: true,
      // No draw animation: the initial render is followed by hideSeries()
      // redraws (the lines start hidden), and animating every pass makes
      // opening the card visibly stutter.
      animations: {
        enabled: false,
      },
      foreColor: labelColor,
      events: {
        mouseMove: function (e: any, chartContext: any) {
          // Which way to nudge the tooltip clear of the pointer. ApexCharts
          // draws the box to the right of the hovered slot on the left half of
          // the chart and flips it to the left past the midpoint
          // (Position.js), so the nudge follows that flip — the class is only
          // a direction, the distance lives in _apexcharts.scss.
          const baseEl = chartContext?.w?.globals?.dom?.baseEl
          const tooltipEl = baseEl?.querySelector('.apexcharts-tooltip')
          const gridEl = baseEl?.querySelector('.apexcharts-grid')
          if (!tooltipEl || !gridEl || typeof e?.clientX !== 'number') return
          const grid = gridEl.getBoundingClientRect()
          const onLeftHalf = e.clientX - grid.left < grid.width / 2
          tooltipEl.classList.toggle('fds-tooltip-gap-right', onLeftHalf)
          tooltipEl.classList.toggle('fds-tooltip-gap-left', !onLeftHalf)
        },
        legendClick: function (chartContext, seriesIndex, config) {
          // Remember the toggle so chart rebuilds re-apply the user's choice.
          const name =
            seriesIndex !== undefined
              ? chartContext?.w?.config?.series?.[seriesIndex]?.name
              : undefined
          if (name) {
            if (hiddenSeries.has(name)) {
              hiddenSeries.delete(name)
            } else {
              hiddenSeries.add(name)
            }
          }
          let options = chartContext.w.globals.lastYAxis
          if (seriesIndex && seriesIndex >= assignedCount) {
            options[seriesIndex].show = !options[seriesIndex].show
            // The 5th arg is overwriteInitialConfig, and it MUST stay false.
            // At its default (true) ApexCharts clones the CURRENT series into
            // globals.initialSeries — and a legend-hidden series carries empty
            // data at that moment. From then on isInitialSeriesSameLen() is
            // false, and any hover that captures a series falls into the
            // single-series tooltip branch instead of the shared one
            // (Tooltip.js handleStickyCapturedSeries). Args 2-4 are the
            // documented defaults: redraw, animate, updateSyncedCharts.
            chartContext.updateOptions({yaxis: options}, false, true, true, false)
          }
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: [
      // Assigned series reuse the position/role-class colors, so the plot
      // matches the timeline events and Team Members labels.
      ...assignedSeries.map((s) => s.color),
      // Line series cycle a fixed palette (one color per config measure).
      ...lineSeries.map((_, i) => LINE_COLORS[i % LINE_COLORS.length]),
    ],
    stroke: {
      width: [...assignedSeries.map(() => 1), ...lineSeries.map(() => 3)],
    },
    xaxis: {
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      // Thins the labels without touching `categories`, so the columns keep
      // their spacing and every slot still names its own time in the tooltip.
      tickAmount,
      categories: hours,
    },
    yaxis: [
      // First assigned series carries the visible headcount axis; the rest get
      // hidden linked axes with the same bounds so stacking lines up.
      ...assignedSeries.map((s, i) => ({
        seriesName: s.name,
        show: i === 0,
        min: 0,
        max: assignedMax,
        ...(i === 0 ? {tickAmount: 2, title: {text: 'Assigned Employees'}} : {}),
        labels: assignedIntLabels,
      })),
      // One opposite axis per line, labelled by its config format code and
      // shown only while its line is visible. Line axes follow the remembered
      // legend state, so a rebuilt chart shows the axis of a line the user left
      // visible.
      ...lineSeries.map((s) => ({
        ...axisBounds(s.data),
        tickAmount: 5,
        opposite: true,
        show: !hiddenSeries.has(s.name),
        title: {
          text: s.name,
        },
        labels: {
          formatter: function (val: number) {
            return formatMeasureValue(val, s.format)
          },
          style: {
            colors: labelColor,
            fontSize: '12px',
          },
        },
      })),
    ],
    tooltip: {
      // These three match the ApexCharts defaults; they're spelled out so the
      // "one row per visible series" contract is stated at the call site. What
      // actually protects it is the overwriteInitialConfig=false in the
      // legendClick handler above.
      shared: true,
      intersect: false,
      hideEmptySeries: false,
      // Follows the cursor. Fixing it (position: 'topLeft') parks the box in
      // the far corner, away from the slot being read.
      fixed: {
        enabled: false,
      },
      // ApexCharts anchors the box ~5px off the hovered point, i.e. right under
      // the cursor. This class carries the nudge that clears it; the direction
      // comes from the mouseMove handler above (see _apexcharts.scss).
      cssClass: 'fds-summary-plot-tooltip',
    },
    legend: {},
  }
}
